import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import { useSnackbar } from 'notistack';
import { Select, Table, TableColumnsType } from 'antd';
import Pagination from '../../Pagination';
import Button from '../../Button';
import Loader from '../../Loader';
import config from '../../../lib/config';
import { infoVariant } from '../../../lib/barVariant';
import { doGetClassStudents } from '../../../store/classStudent.slice';
import { getCommonCodeLabel } from '../../../lib/store';
import StartClassDatePicker from '../../StartClassDatePicker';
import { IGetNotifications, INotificationItem } from '../../../models/notification.model';
import useAxios from '../../../hooks/useAxios';
import { getNotifications } from '../../../api/notification.api';
import ErrorResponse from '../../ErrorResponse';
import { IGetNewStudents } from '../../../models/students.model';
import { getNewStudents } from '../../../api/students.api';
import { createStudent } from '../../../api/campusClasses2.api';

interface IProps {
  setTab: (tab: string) => void;
}

const ClassAllocateStudent: FC<IProps> = ({
  setTab,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { currentSemesterCode } = useSelector((state: RootState) => state.auth);
  const { data: res, isLoading, error, execute } = useAxios<IGetNotifications>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetNewStudents>();
  const { data: createRes, isLoading: createIsLoading, error: createError, execute: createExecute } = useAxios();

  const [field, setField] = useState('korName');
  const [keyword, setKeyword] = useState('');
  const [startDate, setStartDate] = useState('');
  const [notificationItemList, setNotificationItemList] = useState<INotificationItem[]>([]);
  const [page, setPage] = useState(1);
  const [params, setParams] = useState<any>({ perPage: config.pageSize });
  const [record, setRecord] = useState<any>();

  useEffect(() => {
    if (chosenClass) {
      execute(getNotifications(chosenClass.classCode));
    }
  }, [chosenClass]);

  useEffect(() => {
    if (chosenClass) {
      doGetStudents();
    }
  }, [chosenClass, params, page]);

  useEffect(() => {
    if (res && !isLoading) {
      setNotificationItemList(res.data);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (createRes && !createIsLoading && chosenClass) {
      dispatch(doGetClassStudents(chosenClass.classCode));
      sessionStorage.setItem('studentKorName', record.studentKorName);
      sessionStorage.setItem('memberCode', record.memberCode);
      setTab('allocateResult');
    }
  }, [createRes, createIsLoading, chosenClass]);

  const doGetStudents = async () => {
    if (!chosenClass || !currentSemesterCode) {
      return;
    }

    execute2(getNewStudents(
      chosenClass.campusCode,
      currentSemesterCode,
      { ...params, pageNo: page },
    ));
  };

  const onAllocate = async (record: any) => {
    const classCode = chosenClass?.classCode;
    const timetableCode = notificationItemList.find(item => item.timetableDate === startDate)?.timetableCode
    if (!classCode || !timetableCode) return;

    if (!startDate) {
      return enqueueSnackbar(t('class.class.message18'), infoVariant);
    }

    const req = {
      studentCode: record.memberCode,
      studySrtDate: startDate,
      srtTimetableCode: timetableCode,
    };

    setRecord(record);
    createExecute(createStudent(classCode, req));
  };

  const onSearch = () => {
    const params: any = {
      perPage: config.pageSize,
    };
    if (field && keyword) {
      params[field] = keyword;
    }

    setParams(params);
    setPage(1);
  };

  const columns: TableColumnsType<any> = [
    {
      title: t('class.class.column29'),
      dataIndex: 'studentKorName',
      key: 'studentKorName',
      align: 'center',
      sorter: (a, b) => a.studentKorName?.localeCompare(b.studentKorName),
    },
    {
      title: t('class.class.column40'),
      dataIndex: 'studentEngName',
      key: 'studentEngName',
      align: 'center',
      sorter: (a, b) => a.studentEngName?.localeCompare(b.studentEngName),
    },
    {
      title: t('class.class.column41'),
      dataIndex: 'webId',
      key: 'webId',
      align: 'center',
      sorter: (a, b) => a.webId?.localeCompare(b.webId),
    },
    {
      title: t('class.class.column31'),
      dataIndex: 'gradeCode',
      key: 'gradeCode',
      align: 'center',
      sorter: (a, b) => a.gradeCode?.localeCompare(b.gradeCode),
      render: (value) => getCommonCodeLabel('SY0006', value, 'ko'),
    },
    {
      title: t('class.class.button4'),
      dataIndex: 'memberCode',
      key: 'memberCode',
      align: 'center',
      render: (value, record) => (
        <Button
          className="btn btn-sm btn-success btn-w55p"
          onClick={() => onAllocate(record)}
          text={t('class.class.button4')}
        />
      ),
    },
  ];

  return (
    <>
      {(isLoading || isLoading2 || createIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={createError} />

      <div className="card card-info">
        <div className="card-body">
          <div className="flex flex-justify-end">
            <div className="w-150p">
              <Select
                className="w-100"
                placeholder={t('common.button.select')}
                value={field}
                onChange={(value) => setField(value)}
              >
                <Select.Option value="korName">{t('class.class.column29')}</Select.Option>
                <Select.Option value="engName">{t('class.class.column40')}</Select.Option>
                <Select.Option value="webId">{t('class.class.column41')}</Select.Option>
              </Select>
            </div>
            <div className="ml-1">
              <input
                type="text"
                className="form-control"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') onSearch();
                }}
              />
            </div>
            <div className="ml-1">
              <Button
                className="btn btn-secondary w-100"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="text-right">
        <span className="mr-1">{t('class.class.column33')}</span>
        <StartClassDatePicker
          notificationItemList={notificationItemList}
          startDate={startDate}
          onStartDateChange={setStartDate}
        />
      </div>

      <div>
        <Table
          columns={columns}
          dataSource={res2?.data}
          pagination={{ pageSize: config.pageSize }}
        />
      </div>
      <div className="row mt-3">
        <div className="col-3">
          <Button
            className="btn btn-sm btn-primary btn-w100p"
            onClick={() => setTab('register')}
            text={t('common.button.back')}
          />
        </div>
        <div className="col-6">
          <div className="common-paging center">
            <Pagination
              count={res2?.total || 0}
              page={page}
              setPage={setPage}
            />
          </div>
        </div>
        <div className="col-3" />
      </div>
    </>
  );
};

export default ClassAllocateStudent;
